// Send-dialog greeting rule (CEO Jake, 2026-08-05: "Presentations - I like Hi
// (Name)" · "Let the standard Hi (Name) be there for all emails, and if the rep
// sends to more than one - they can change it themselves").
//
// ONE rule, one branch: the greeting names the FIRST currently-selected
// recipient; when that recipient has no usable first name it falls back to
// "there". There is deliberately NO multi-recipient variant — the rep owns the
// body and edits the greeting if they are writing to a group.
//
// WHY THIS IS PURE AND SHARED. Round 66 fixed a real bug: the greeting was
// baked in at compose time from the client's PRIMARY contact and never updated
// when the rep changed recipients, so unchecking the primary sent an email
// opening "Hi {primary's first name}," to a different human. The fix that keeps
// that bug dead is DERIVING LIVE from the current selection rather than caching
// a name in state — which means the derivation has to run in the browser, on
// every selection change. Keeping it here (rather than inline in the modal)
// gives the API's /send fallback path the same definition and lets the offline
// gate EXECUTE the branches instead of regex-matching component source.

/**
 * A recipient as the greeting rule sees it. `firstName` is the structured field
 * (a client contact row); `name` is the free-form display name a rep typed into
 * the "Also send to" row. Either may be absent — that is the "no usable first
 * name" branch, not an error.
 */
export interface GreetingRecipient {
  firstName?: string | null;
  name?: string | null;
  /** A building's shared inbox (e.g. ashlarpark@bozzuto.com) — no person, so
   *  the email opens "Hi!" (Stephanie 2026-10-30). */
  buildingEmail?: boolean;
}

/** The contact tag that marks a building's shared inbox (Stephanie 2026-10-30:
 *  "if it's a building email there doesn't need to be a name and all emails
 *  only say Hi!"). Stored in contacts.tags — no database change. */
export const BUILDING_EMAIL_TAG = "building-email";

export function isBuildingEmail(tags: readonly string[] | null | undefined): boolean {
  return !!tags && tags.includes(BUILDING_EMAIL_TAG);
}

/** The greeting for a building's shared inbox. */
export const BUILDING_EMAIL_GREETING = "Hi!";

/** Greeting name when nobody usable is selected → renders "Hi there,". */
export const SEND_GREETING_FALLBACK = "there";

/**
 * The first name to greet a single recipient by, or null when there isn't one.
 * Takes the first whitespace-delimited token so a free-form "Jane Doe" greets
 * "Jane". An address pasted into a name field is NOT a name — greeting someone
 * "Hi boss@client.com," is worse than greeting them generically.
 */
function usableFirstName(r: GreetingRecipient | undefined): string | null {
  if (!r) return null;
  const explicit = (r.firstName ?? "").trim();
  const source = explicit.length > 0 ? explicit : (r.name ?? "").trim();
  if (source.length === 0) return null;
  const token = source.split(/\s+/)[0] ?? "";
  if (token.length === 0 || token.includes("@")) return null;
  return token;
}

/**
 * The name for renderPlainEmail's `greetingName` — the first selected
 * recipient's first name, else "there". Order of `recipients` is the order the
 * rep sees in the dialog (contacts/presets top-down, then the ad-hoc chips), so
 * "first" means the same thing to the code and to the human.
 */
export function deriveGreetingName(
  recipients: readonly GreetingRecipient[],
): string {
  return usableFirstName(recipients[0]) ?? SEND_GREETING_FALLBACK;
}

/**
 * The literal greeting LINE the dialog pre-fills. Must stay byte-identical to
 * what renderPlainEmail emits for the same name (`Hi ${name},`) or the dialog
 * would show the rep something other than what an unedited send produces — the
 * offline gate asserts the two agree by executing both.
 */
export function deriveGreetingLine(
  recipients: readonly GreetingRecipient[],
): string {
  // More than one person on the email → just "Hi!" (Stephanie 2026-11-02:
  // "we also decided if there was more than 1 person it would just say Hi!").
  if (recipients.length > 1) return BUILDING_EMAIL_GREETING;
  if (recipients[0]?.buildingEmail) return BUILDING_EMAIL_GREETING;
  return `Hi ${deriveGreetingName(recipients)},`;
}

/**
 * Is this paragraph a greeting we may SEED over — "Hi {a name}," and not a
 * sentence? Used at SEED time only, to decide whether the body's first paragraph
 * is the template's greeting slot. It is NOT the may-I-rewrite test: the dialog
 * decides that by exact-matching what it last wrote, which is the only test that
 * can tell "the rep retyped this line" from "the rep edited the body".
 *
 * DELIBERATELY NARROW. `/^Hi\b[^,]*,$/` was loose in the direction that matters
 * — it swallowed a whole sentence ("Hi thanks so much for choosing us for your
 * order today,") and a hyphenated opener ("Hi-fi enthusiasts,"), either of which
 * we would then have overwritten with "Hi Amy,". So:
 *   - a literal space must follow "Hi" (kills "Hi-fi …" and "Hithere,");
 *   - the name is at most THREE comma-free tokens, which covers every form the
 *     template emits ("Hi there," · "Hi Amy," · "Hi Acme Co team,") and nothing
 *     that reads as prose.
 * Whitespace is normalised first because contenteditable hands back &nbsp;.
 */
export function isGreetingLine(text: string): boolean {
  const t = text.replace(/\s+/g, " ").trim();
  return t === BUILDING_EMAIL_GREETING || /^Hi [^\s,]+(?: [^\s,]+){0,2},$/.test(t);
}
